/**
 * ============================================================
 * KEJJA OS — src/pages/notifications/NotificationPreferencesPage.tsx
 * ============================================================
 */

import { useMemo } from 'react';
import { Bell, Mail, Smartphone, Volume2, AlertCircle } from 'lucide-react';
import {
  useNotificationPreferences, useUpsertPreference,
} from '@/hooks/useNotifications';
import type {
  EventTypeMeta, NotificationPreference, DigestMode,
} from '@/lib/notificationsApi';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import PushSetupCard from './PushSetupCard';
import { cn } from '@/lib/utils';

export default function NotificationPreferencesPage() {
  const { data, isLoading } = useNotificationPreferences();
  const upsertMut = useUpsertPreference();

  const catalog = data?.catalog ?? [];
  const prefs = data?.preferences ?? [];
  const prefByEvent = useMemo(() => {
    const m = new Map<string, NotificationPreference>();
    for (const p of prefs) m.set(p.eventType, p);
    return m;
  }, [prefs]);

  if (isLoading) return <Skeleton className="h-96" />;

  // Group catalog by module
  const grouped = new Map<string, EventTypeMeta[]>();
  for (const e of catalog) {
    const arr = grouped.get(e.module) ?? [];
    arr.push(e);
    grouped.set(e.module, arr);
  }

  return (
    <div className="space-y-5 pb-16">
      <PushSetupCard />
      <div className="bg-muted/40 rounded-lg p-3 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-muted-foreground">
          In-app + sound + email + push all work. Test each channel below.
        </p>
      </div>

      {Array.from(grouped.entries()).map(([module, events]) => (
        <section key={module}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
            <Bell className="w-3 h-3" /> {module}
          </h3>
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {events.map((evt) => (
                <EventRow
                  key={evt.key}
                  event={evt}
                  pref={prefByEvent.get(evt.key)}
                  onToggle={(patch) => upsertMut.mutate({ eventType: evt.key, ...patch })}
                />
              ))}
            </CardContent>
          </Card>
        </section>
      ))}
    </div>
  );
}

// ============================================================
// EventRow
// ============================================================

function EventRow({
  event, pref, onToggle,
}: {
  event: EventTypeMeta;
  pref: NotificationPreference | undefined;
  onToggle: (patch: {
    inAppEnabled?: boolean; emailEnabled?: boolean; pushEnabled?: boolean; soundEnabled?: boolean;
    digestMode?: DigestMode;
  }) => void;
}) {
  // Default to enabled + sound on
  const inApp = pref?.inAppEnabled ?? true;
  const email = pref?.emailEnabled ?? false;
  const push = pref?.pushEnabled ?? false;
  const sound = pref?.soundEnabled ?? true;

  return (
    <div className="p-3">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{event.label}</span>
            {event.defaultPriority === 'critical' && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 font-semibold">
                CRITICAL
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">{event.description}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <ChannelToggle
          icon={Bell} label="In-app" enabled={inApp}
          onChange={(v) => onToggle({ inAppEnabled: v })}
        />
        <ChannelToggle
          icon={Volume2} label="Sound" enabled={sound} disabled={!inApp}
          onChange={(v) => onToggle({ soundEnabled: v })}
        />
        <ChannelToggle
          icon={Mail} label="Email" enabled={email}
          onChange={(v) => onToggle({ emailEnabled: v })}
        />
        <ChannelToggle
          icon={Smartphone} label="Push" enabled={push} tag="soon"
          onChange={(v) => onToggle({ pushEnabled: v })}
        />
      </div>
    </div>
  );
}

function ChannelToggle({
  icon: Icon, label, enabled, onChange, disabled, tag,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string; enabled: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  tag?: string;
}) {
  return (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors',
        enabled && !disabled
          ? 'border-amber-500/60 bg-amber-500/10 text-amber-700 dark:text-amber-300'
          : 'border-border text-muted-foreground',
        disabled && 'opacity-40 cursor-not-allowed',
      )}
    >
      <Icon className="w-3 h-3" />
      {label}
      {tag && <span className="text-[8px] opacity-70 uppercase">({tag})</span>}
    </button>
  );
}
